import { useQueryClient } from "@tanstack/react-query";
import {
  Link2,
  PlusIcon,
  X,
  FileText,
  Building2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Radar,
  Sparkles,
  ArrowLeft,
  Route,
  Cpu,
  Hand,
  CloudUpload,
  TriangleAlert,
} from "lucide-react";
import React, {
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  lazy,
  Suspense,
} from "react";

import FileUpload from "./DocUpload";
import PreparingGeneration from "./PreparingGeneration";
import { RequirementTooltip } from "./RequirementTooltip";

import type { TestCaseGenerationRef } from "./index";

import { bindSuggestionChips } from "@/common/components/ai/suggestionPrompt";
import { SuggestionChips } from "@/common/components/ai/SuggestionChips";
import { EntityLinkDialog } from "@/common/components/link-dialog/EntityLinkDialog";
import AlertDialog from "@/common/components/popups/AlertDialog";
import { ScannerPanel } from "@/common/components/scanner";
import { useAuth } from "@/common/hooks/useAuth";
import { useBackgroundGenerationActions } from "@/common/providers/BackgroundGenerationProvider";
import { resolveGenerationGate } from "@/common/services/ai-generation";
import {
  useSubscription,
  AI_PAYWALL_PAYLOAD,
  openPaywallModal,
} from "@/common/services/billing";
import {
  useRequirementById,
  useRequirementsByIds,
} from "@/common/services/requirement";
import { SiteSyncStatus } from "@/common/services/site-sync/site-sync.api";
import {
  getSiteSyncStatus,
  getSiteSyncPages,
} from "@/common/services/site-sync/site-sync.api";
import {
  useSiteSyncJourneys,
  useSiteSyncList,
  SITE_SYNC_QUERY_KEY,
  SITE_SYNC_LIST_KEY,
} from "@/common/services/site-sync/site-sync.queries";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const TestCaseGeneration = lazy(() => import("./index"));

type OutputMode = "manual" | "automation";

interface SyncedGeneratorModalProps {
  open: boolean;
  onClose: () => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  handleFileUpload: (file: File | null) => void;
  onRequirementIds?: (ids: number[]) => void;
  initialRequirementId?: number | null;
  /** Pre-link a batch of requirements (e.g. every Ready requirement in a folder). */
  initialRequirementIds?: number[];
  syncStatus: SiteSyncStatus;
  appUrl: string;
  projectName: string;
  pages: any[];
  outputMode: OutputMode;
  onOutputModeChange: (mode: OutputMode) => void;
  projectId?: number | null;
  /** When true, auto-open the AI Application Scanner on modal open (for un-synced projects) */
  forceRescanOnOpen?: boolean;
  pathToActiveNode?: any[];
  activeFolderId?: number | null;
  onGenerationComplete?: () => void;
  onSelectedTestCases?: (value: any[]) => void;
  /** Viewport point (icon centre) the modal should visually open out of. */
  originPoint?: { x: number; y: number } | null;
}

function formatTimeAgo(dateStr?: string): string {
  if (!dateStr) return "Unknown";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-white p-3 rounded-md border border-border">
      <p className="text-[10.5px] text-muted-foreground font-medium mb-1 uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cn(
          "text-xl leading-none font-semibold tracking-tight tabular-nums",
          accent ? "text-success" : "text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function ToolbarButton({
  children,
  icon,
  onClick,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground hover:bg-muted rounded-md transition-colors cursor-pointer"
    >
      <span className="text-muted-foreground">{icon}</span>
      {children}
    </button>
  );
}

const SyncedGeneratorModal: React.FC<SyncedGeneratorModalProps> = (props) => {
  // Don't mount anything when modal is closed — prevents all hooks from running
  if (!props.open) return null;
  return <SyncedGeneratorModalInner {...props} />;
};

const SyncedGeneratorModalInner: React.FC<SyncedGeneratorModalProps> = ({
  open,
  onClose,
  prompt,
  onPromptChange,
  handleFileUpload,
  onRequirementIds,
  initialRequirementId,
  initialRequirementIds,
  syncStatus,
  appUrl,
  projectName,
  pages,
  outputMode,
  onOutputModeChange,
  projectId,
  forceRescanOnOpen,
  pathToActiveNode = [],
  activeFolderId = null,
  onGenerationComplete,
  onSelectedTestCases,
  originPoint,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAllPages, setShowAllPages] = useState(false);
  const [reqPickerOpen, setReqPickerOpen] = useState(false);
  const [linkedReqs, setLinkedReqs] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Controls the universal AI Application Scanner overlay dialog. Auto-opens for
  // un-synced projects (forceRescanOnOpen); also opened via "Re-scan Application".
  const [scannerOpen, setScannerOpen] = useState(() => !!forceRescanOnOpen);

  // Inline generation state
  const [showGenerating, setShowGenerating] = useState(false);
  const [inlineGenerating, setInlineGenerating] = useState(false);
  const [inlineCompleted, setInlineCompleted] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlineSaveSuccess, setInlineSaveSuccess] = useState(false);
  const [inlineSelectedTests, setInlineSelectedTests] = useState<any[]>([]);
  // Confirmation before discarding a finished-but-unsaved generation via the
  // header X (the generated cases are lost unless the user saves them first).
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const inlineGeneratorRef = useRef<TestCaseGenerationRef>(null);
  const pendingInlineTrigger = useRef(false);
  // Safety net: if generation never reports it has started (silent failure,
  // ref never mounts, missing input, etc.) clear the Preparing overlay so the
  // user is never stuck staring at it — the underlying generator UI shows
  // through, letting them see the form/error and retry.
  const [prepExpired, setPrepExpired] = useState(false);

  // ── Open-from-icon entry animation ──────────────────────────────────────────
  // Make the dialog appear to grow out of the AI icon that opened it. We measure
  // the card's natural box, set transform-origin to the icon point (in the card's
  // own coordinate space), then run a GPU-composited scale/opacity animation via
  // the Web Animations API. `fill: "backwards"` applies the first keyframe on the
  // very first painted frame, so there's no flash and no layout thrash. Runs once
  // on mount (the modal fully unmounts when closed).
  const cardRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || typeof card.animate !== "function") return;
    // Respect reduced-motion — skip the emerge-from-icon scale entirely.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches)
      return;
    let origin = "center center";
    if (originPoint) {
      const rect = card.getBoundingClientRect();
      origin = `${originPoint.x - rect.left}px ${originPoint.y - rect.top}px`;
    }
    card.style.transformOrigin = origin;
    const anim = card.animate(
      [
        { opacity: 0, transform: "scale(0.4)" },
        { opacity: 1, transform: "scale(1)" },
      ],
      {
        duration: 300,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
        fill: "backwards",
      },
    );
    return () => anim.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId;
  const queryClient = useQueryClient();
  const bgGen = useBackgroundGenerationActions();

  // Client-side entitlement pre-check (defense-in-depth UX; the backend
  // remains the authoritative enforcement point — see requireAiEntitlement).
  // Only treat the feature as *confirmed* locked when the query has actually
  // resolved to `false`; while loading/erroring we defer to the backend's 402
  // rather than guessing, matching the existing AiLockOverlay behaviour.
  const { data: subscriptionData } = useSubscription({ enabled: !!userPreferenceData });
  const aiEnabled = subscriptionData?.data?.aiEnabled !== false;

  // Reopen straight into the live generation: when the modal opens and a
  // background session exists, jump to the generating view WITHOUT starting a new
  // generation. The embedded generator reattaches (reconnects SSE + replays) on
  // mount. This is what makes clicking the background spinner seamless.
  React.useEffect(() => {
    if (!open) return;
    const s = bgGen?.getSession();
    if (s && (s.status === "running" || s.status === "complete")) {
      // Restore the generator mode so a backgrounded crawl (automation) reopens
      // into the automation view, not manual (#1876 #17).
      if (s.mode === "automation" && outputMode !== "automation") {
        onOutputModeChange("automation");
      } else if (s.mode === "manual" && outputMode !== "manual") {
        onOutputModeChange("manual");
      }
      pendingInlineTrigger.current = false;
      setScannerOpen(false);
      setShowGenerating(true);
      setInlineGenerating(s.status === "running");
      setInlineCompleted(s.status === "complete");
      setInlineSaveSuccess(false);
    }
    // Only react to the open transition; session is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch list of all synced applications for this project
  const { data: syncListData } = useSiteSyncList(versionId);
  const syncedApps =
    syncListData?.syncs?.filter((s) => s.status === "synced") || [];

  // Selected app override — null means use parent's syncStatus (latest/default)
  const [selectedSyncUrl, setSelectedSyncUrl] = useState<string | null>(null);
  const [overrideSyncStatus, setOverrideSyncStatus] = useState<any>(null);
  const [overridePages, setOverridePages] = useState<any[] | null>(null);
  const [isOverrideFetching, setIsOverrideFetching] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(
    null,
  );
  const skipNextOverrideFetch = useRef(false);

  // When user selects a different app, fetch its data
  React.useEffect(() => {
    if (!selectedSyncUrl || !versionId) {
      setOverrideSyncStatus(null);
      setOverridePages(null);
      setIsOverrideFetching(false);
      return;
    }
    // Skip fetch if data was already provided via onScannedUrl
    if (skipNextOverrideFetch.current) {
      skipNextOverrideFetch.current = false;
      return;
    }
    let cancelled = false;
    setIsOverrideFetching(true);
    setOverrideSyncStatus(null);
    setOverridePages(null);
    (async () => {
      try {
        const statusRes = await getSiteSyncStatus(versionId, selectedSyncUrl);
        if (cancelled) return;
        setOverrideSyncStatus(statusRes.data);
        if (statusRes.data?.syncId) {
          const pagesRes = await getSiteSyncPages(statusRes.data.syncId);
          if (!cancelled) setOverridePages(pagesRes.data?.pages || []);
        }
      } catch {
        /* ignore */
      }
      if (!cancelled) setIsOverrideFetching(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSyncUrl, versionId]);

  // Guards the auto-decision to a single resolution per open (reset in the
  // open-reset effect below). Declared here so the open-reset effect can clear it.
  const autoRescanResolvedRef = React.useRef(false);

  // Reset per-open state whenever the modal (re)opens. `scannerOpen` is a plain
  // useState initialised once at mount, so without this it would retain whatever
  // value it had at the last close (e.g. the user had clicked "Re-scan"). Reset
  // it to the caller's requested default and let the decision effect below
  // correct it against the authoritative sync list.
  React.useEffect(() => {
    if (open) {
      setSelectedSyncUrl(null);
      autoRescanResolvedRef.current = false;
      setScannerOpen(!!forceRescanOnOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Effective values — when a different app is selected, DON'T fall back to parent data
  const isOverrideLoading = isOverrideFetching;
  const effectiveSyncStatus = selectedSyncUrl ? overrideSyncStatus : syncStatus;
  // Always use the actual sync's URL (not the stale appUrl prop) when no override
  const effectiveAppUrl = selectedSyncUrl || syncStatus?.appUrl || appUrl;
  const effectivePages = selectedSyncUrl ? (overridePages ?? []) : pages;

  // Decide wizard-vs-generator from the modal's OWN authoritative sync list, not
  // from the caller's one-shot `forceRescanOnOpen` snapshot. That snapshot is
  // derived from a single status query that is racy (still loading at mount) and
  // can return a non-synced row (the backend's default probe orders by most
  // recently updated crawl). The sync LIST (`useSiteSyncList`) is the ground
  // truth for "does a synced app exist for this version". Rules:
  //   • Wait until the list has actually loaded before deciding (avoid a wrong
  //     early flip based on empty data).
  //   • If a synced app exists anywhere → never show the wizard; land on the
  //     generator and point it at that app so the prompt view has real data.
  //   • Only honour the caller's forced wizard when there is genuinely NO synced
  //     app to generate against.
  // The ref limits this to a single resolution per open so it never yanks the
  // user out of a re-scan they opened themselves via "Re-scan Application".
  React.useEffect(() => {
    if (!open) return;
    if (autoRescanResolvedRef.current) return;
    // Sync list still loading — keep the initial state and decide once it lands.
    if (syncListData === undefined) return;

    // "Has context" = the crawl discovered pages, regardless of the exact status
    // string ("failed"/"auth_required" rows can still hold usable pages). Treat
    // those as synced-enough so we land on the generator prompt rather than
    // force-opening the scanner over an app that already has context.
    const parentHasContext = (effectiveSyncStatus?.pageCount ?? 0) > 0;
    const listSyncedApp =
      syncedApps.find((a) => (a.pageCount ?? 0) > 0) ?? syncedApps[0] ?? null;

    autoRescanResolvedRef.current = true;
    if (parentHasContext || listSyncedApp) {
      if (!parentHasContext && listSyncedApp && !selectedSyncUrl) {
        // Point the modal at the existing synced app so the prompt view has data.
        setSelectedSyncUrl(listSyncedApp.appUrl);
      }
      setScannerOpen(false);
    } else {
      // No synced app anywhere — honour the caller's request (a real scan is the
      // only way to get app context; requirement-based generation still works).
      setScannerOpen(!!forceRescanOnOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, forceRescanOnOpen, effectiveSyncStatus, syncListData]);

  // Fetch discovered journeys from crawl agent brain
  const { data: journeysData } = useSiteSyncJourneys(
    effectiveSyncStatus?.syncId && effectiveSyncStatus.status === "synced"
      ? effectiveSyncStatus.syncId
      : undefined,
  );
  const journeys = journeysData?.journeys || [];
  const selectedJourney = selectedJourneyId
    ? journeys.find((j: any) => j.id === selectedJourneyId) || null
    : null;

  const buildJourneyPrompt = React.useCallback((journey: any) => {
    const stepsDesc = (journey.steps || [])
      .map((s: any) =>
        `${s.order}. ${s.action?.type || "do"} ${s.action?.target ? `"${s.action.target}"` : ""} ${s.action?.value ? `with "${s.action.value}"` : ""} → ${s.expectedResult || ""}`.trim(),
      )
      .join("\n");
    return `${journey.name}${journey.description ? ": " + journey.description : ""}\n\nSteps:\n${stepsDesc}`;
  }, []);

  React.useEffect(() => {
    if (!selectedJourney) return;
    const expectedPrompt = buildJourneyPrompt(selectedJourney);
    if (prompt !== expectedPrompt) {
      setSelectedJourneyId(null);
    }
  }, [prompt, selectedJourney, buildJourneyPrompt]);

  const handleSelectJourney = (journey: any) => {
    const promptText = buildJourneyPrompt(journey);
    onPromptChange(promptText);
    setSelectedJourneyId(journey.id || null);
  };

  // Smart test case suggestions from discovered pages + journeys
  const testCaseSuggestions = useMemo(() => {
    if (!effectivePages || effectivePages.length === 0) return [];
    const suggestions: string[] = [];

    // Priority 1: Journey-based suggestions (from crawl agent brain)
    for (const journey of journeys.slice(0, 4)) {
      const label = journey.name || journey.description;
      if (label) suggestions.push(label);
    }

    // Analyze pages to suggest comprehensive test scenarios
    for (const page of effectivePages.slice(0, 10)) {
      const pageName = page.name || page.routePath?.split("/").pop() || "";
      if (!pageName) continue;

      const elements = page.elements || [];
      const formFields = elements.filter(
        (el: any) => el.role === "textbox" || el.role === "combobox",
      );
      const buttons = elements.filter((el: any) => el.role === "button");

      if (formFields.length > 0 && buttons.length > 0) {
        suggestions.push(
          `Test the ${pageName} form submission with valid and invalid data`,
        );
        suggestions.push(
          `Verify ${pageName} form validation for required fields`,
        );
      }
      if (page.isAuthProtected) {
        suggestions.push(
          `Test ${pageName} page access control and authentication`,
        );
      }
    }

    // Add cross-page navigation tests
    if (effectivePages.length > 3) {
      suggestions.push("Test navigation flow between all main pages");
    }

    // Add E2E scenario suggestions
    const contactPage = effectivePages.find((p: any) =>
      (p.name || "").toLowerCase().includes("contact"),
    );
    const loginPage = effectivePages.find((p: any) =>
      (p.name || "").toLowerCase().includes("login"),
    );
    if (contactPage)
      suggestions.push(
        "End-to-end: Submit Contact Us form and verify confirmation",
      );
    if (loginPage)
      suggestions.push(
        "End-to-end: Login flow with valid and invalid credentials",
      );

    return Array.from(new Set(suggestions)).slice(0, 6);
  }, [effectivePages, journeys]);

  const suggestionChips = bindSuggestionChips(prompt, onPromptChange);

  const { data: initialReqData } = useRequirementById(
    initialRequirementId ?? 0,
    {
      enabled:
        !!initialRequirementId &&
        !linkedReqs.some((r) => r.id === initialRequirementId),
    },
  );

  React.useEffect(() => {
    if (
      initialReqData?.data &&
      !linkedReqs.some((r) => r.id === initialReqData.data.id)
    ) {
      const updated = [...linkedReqs, initialReqData.data];
      setLinkedReqs(updated);
      onRequirementIds?.(updated.map((r) => r.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReqData]);

  // Batch-seed a folder's requirements (all Ready requirements under a folder).
  const { data: initialReqsMap } = useRequirementsByIds(
    initialRequirementIds ?? [],
    versionId,
    {
      enabled: !!(initialRequirementIds && initialRequirementIds.length),
    },
  );
  const seededBatchRef = React.useRef(false);
  React.useEffect(() => {
    if (seededBatchRef.current) return;
    if (!initialReqsMap || !initialRequirementIds?.length) return;
    const seeded = (initialRequirementIds ?? [])
      .map((id) => initialReqsMap.get(Number(id)))
      .filter(Boolean) as any[];
    if (seeded.length === 0) return;
    seededBatchRef.current = true;
    const existing = new Set(linkedReqs.map((r) => r.id));
    const merged = [
      ...linkedReqs,
      ...seeded.filter((r) => !existing.has(r.id)),
    ];
    setLinkedReqs(merged);
    onRequirementIds?.(merged.map((r) => r.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReqsMap]);

  // Track the uploaded doc locally (for the context-aware placeholder + gating)
  // while still forwarding the file to the parent handler.
  const handleFileUploadLocal = (file: File | null) => {
    setUploadedFile(file);
    handleFileUpload(file);
  };

  // When a requirement is linked and/or a document is uploaded, the model has
  // enough context to generate without a typed prompt — so make the prompt
  // optional and surface a context-aware placeholder explaining what will be
  // used (and how an extra typed prompt steers it).
  const hasRequirement = linkedReqs.length > 0;
  const hasDoc = !!uploadedFile;
  const hasContext = hasRequirement || hasDoc;

  const reqIdsLabel = linkedReqs
    .slice(0, 2)
    .map((r) => `REQ-${String(r.id).padStart(4, "0")}`)
    .join(", ")
    .concat(linkedReqs.length > 2 ? ` +${linkedReqs.length - 2}` : "");

  const promptPlaceholder = (() => {
    if (hasRequirement && hasDoc)
      return `Leave empty to generate tests from ${reqIdsLabel} + the uploaded document, or add focus areas to steer it…`;
    if (hasRequirement)
      return `Leave empty to generate tests from ${reqIdsLabel}, or add focus areas to steer it…`;
    if (hasDoc)
      return "Leave empty to generate tests from the uploaded document, or add focus areas to steer it…";
    return "e.g. Verify the user login flow with MFA enabled, including edge cases for expired tokens…";
  })();

  // Start inline generation (switch modal to generating view)
  const handleStartInlineGeneration = () => {
    setShowGenerating(true);
    setScannerOpen(false);
    pendingInlineTrigger.current = true;
  };

  // Auto-trigger generation once the TestCaseGeneration component mounts.
  // The generator is lazy-loaded, so its imperative ref may not exist for a few
  // hundred ms. Poll until the ref is ready instead of using a fixed delay,
  // otherwise the "Preparing generation…" screen can hang forever.
  React.useEffect(() => {
    if (!pendingInlineTrigger.current || !showGenerating) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 100; // ~10s safety net (100 × 100ms)

    const tryTrigger = () => {
      if (cancelled || !pendingInlineTrigger.current) return;
      const ref = inlineGeneratorRef.current;
      if (ref?.triggerGeneration) {
        pendingInlineTrigger.current = false;
        ref.triggerGeneration();
        return;
      }
      if (attempts++ < maxAttempts) {
        timer = setTimeout(tryTrigger, 100);
      }
    };

    let timer = setTimeout(tryTrigger, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showGenerating]);

  // Prep-overlay safety timeout — clears the Preparing screen if generation
  // hasn't begun streaming within the window (after the ref-poll has had time
  // to give up). Resets whenever we leave the generating view or generation
  // actually starts.
  React.useEffect(() => {
    if (!showGenerating) {
      setPrepExpired(false);
      return;
    }
    if (inlineGenerating || inlineCompleted) {
      setPrepExpired(false);
      return;
    }
    const t = setTimeout(() => setPrepExpired(true), 12000);
    return () => clearTimeout(t);
  }, [showGenerating, inlineGenerating, inlineCompleted]);

  const handleInlineStopGenerating = () => {
    inlineGeneratorRef.current?.stopGeneration();
    setInlineGenerating(false);
    setInlineCompleted(true);
  };

  const handleInlineStartOver = () => {
    // Stop the in-flight generation so cases don't keep streaming/saving in the
    // background after the user goes back to the prompt.
    inlineGeneratorRef.current?.stopGeneration();
    setShowGenerating(false);
    setInlineGenerating(false);
    setInlineCompleted(false);
    setInlineSelectedTests([]);
  };

  const handleInlineSave = () => {
    setInlineSaving(true);
    inlineGeneratorRef.current?.saveGeneration(inlineSelectedTests, () => {
      setInlineSaving(false);
      setInlineSaveSuccess(true);
    });
  };

  const handleInlineCloseAndSaveInBackground = () => {
    // Kick off a fire-and-forget save (survives this modal unmounting) and close
    // immediately so the user isn't blocked by the high-latency bulk save.
    inlineGeneratorRef.current?.saveGenerationInBackground(inlineSelectedTests);
    onClose();
  };

  // True when the AI has finished generating and there are unsaved test cases on
  // screen — closing now would discard them, so the header X must confirm first.
  const hasUnsavedGeneration =
    showGenerating && inlineCompleted && !inlineSaving && !inlineSaveSuccess;

  // Header X handler: guard an unsaved finished generation behind a confirm
  // dialog; otherwise (input/generating/saved states) close straight away.
  const handleHeaderClose = () => {
    if (hasUnsavedGeneration) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  // Leaving the inline scanner (Done, or the header X while scanning) returns to
  // the generator prompt — the scanner is a sub-mode of this one dialog, not a
  // separate window. A crawl may have completed, so refresh the sync context
  // (status badge, discovered pages, app list) on the way out.
  //
  // EXCEPTION: if there is still no usable sync anywhere for this project (the
  // scanner was forced open because nothing has ever been synced, and the user
  // dismissed it without finishing a scan), the prompt view would be a dead
  // end — Generate stays disabled and the only action is to reopen the
  // scanner. Close the whole dialog instead of landing there.
  const handleScannerClose = () => {
    queryClient.invalidateQueries({ queryKey: [SITE_SYNC_QUERY_KEY] });
    queryClient.invalidateQueries({
      queryKey: [SITE_SYNC_LIST_KEY, versionId],
    });
    const hasUsableSyncAnywhere =
      hasSyncContext || syncedApps.some((a) => (a.pageCount ?? 0) > 0);
    if (!hasUsableSyncAnywhere) {
      onClose();
      return;
    }
    setScannerOpen(false);
  };

  // User confirmed the discard: fully reset the generation (aborts any stream,
  // clears the background session/green-dot, wipes results) and close.
  const handleConfirmDiscard = () => {
    inlineGeneratorRef.current?.resetGenerationState();
    setShowGenerating(false);
    setInlineGenerating(false);
    setInlineCompleted(false);
    setInlineSelectedTests([]);
    setDiscardConfirmOpen(false);
    onClose();
  };

  // Use badge data for instant page count while loading
  const selectedAppBadge = selectedSyncUrl
    ? syncedApps.find((a) => a.appUrl === selectedSyncUrl)
    : null;
  const pageCount =
    effectiveSyncStatus?.pageCount ??
    selectedAppBadge?.pageCount ??
    effectivePages.length;
  const interactionCount = effectiveSyncStatus?.interactionCount ?? 0;
  const authConfigured =
    effectiveSyncStatus?.authStrategy &&
    effectiveSyncStatus.authStrategy !== "none";
  const lastSynced = formatTimeAgo(effectiveSyncStatus?.lastSyncedAt);
  // "Has context" = the crawl actually discovered pages. We key the ready/active
  // UI (green dot, "Synced" pill, discovered-pages list) off pageCount rather
  // than the exact status string: a "failed"/"auth_required" row can still hold
  // usable pages (e.g. an interrupted crawl that found 26 routes), and the
  // backend already self-heals a "synced" row with 0 pages to "failed". So
  // pageCount > 0 is the single source of truth for "this app has AI context".
  const hasSyncContext = pageCount > 0;
  // Interrupted-but-usable crawl (backend reports status "synced" + partial).
  const isPartialSync = hasSyncContext && !!effectiveSyncStatus?.partial;
  // A crawl is actively running for the selected app — generation and a second
  // scan must both be blocked until it settles (success, failure, or cancel).
  const syncInProgress = effectiveSyncStatus?.status === "crawling";

  // ── Generate click — centralised decision order ────────────────────────────
  // entitlement → sync state → generate, shared with the Requirement
  // generator via resolveGenerationGate so both can't drift apart.
  const handleGenerateClick = () => {
    const decision = resolveGenerationGate({
      aiEnabled,
      syncInProgress,
      hasSyncContext,
    });
    switch (decision) {
      case "entitlement_denied":
        // Confirmed-locked: never start generation or open the scanner for a
        // plan that can't use AI at all — just surface the same upgrade modal
        // the backend's 402 would show, without a round-trip.
        openPaywallModal(AI_PAYWALL_PAYLOAD);
        return;
      case "sync_in_progress":
        // Belt-and-braces — the button is disabled in this state too, but
        // guard against a stray click slipping through before the disabled
        // re-render.
        return;
      case "needs_sync":
        // Eligible, but the application hasn't been synchronised yet — make
        // the scanner the primary workflow instead of opening a generator
        // that has nothing to work with.
        setScannerOpen(true);
        return;
      case "ready":
        handleStartInlineGeneration();
    }
  };


  return (
    <>
      {/* Backdrop — matches the app's standard dialog overlay so focus stays on the modal */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 pt-[5vh]">
        <section
          ref={cardRef}
          data-tour="ai-generator-modal"
          aria-label="AI Test Case Generator"
          className="bg-white rounded-lg border border-border shadow-lg overflow-hidden flex flex-col max-h-[90vh]"
          style={{
            width: scannerOpen
              ? "min(1120px, 96vw)"
              : showGenerating
                ? "min(1320px, 97vw)"
                : "min(740px, 95vw)",
            height: showGenerating ? "90vh" : undefined,
            transition:
              "width 350ms cubic-bezier(0.4, 0, 0.2, 1), height 350ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Header */}
          <header className="px-5 py-2.5 flex items-center justify-between border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              {scannerOpen ? (
                <>
                  <Radar className="h-4 w-4 text-info" strokeWidth={2} />
                  <h2 className="text-[13px] font-semibold text-foreground">
                    AI Application Scanner
                  </h2>
                </>
              ) : (
                <>
                  <Sparkles
                    className="h-4 w-4 text-muted-foreground"
                    strokeWidth={2}
                  />
                  <h2 className="text-[13px] font-semibold text-foreground">
                    AI Test Case Generator
                  </h2>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium capitalize",
                      outputMode === "automation"
                        ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200"
                        : "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
                    )}
                  >
                    {outputMode === "automation" ? (
                      <Cpu className="h-3 w-3" strokeWidth={2.25} />
                    ) : (
                      <Hand className="h-3 w-3" strokeWidth={2.25} />
                    )}
                    {outputMode}
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={scannerOpen ? handleScannerClose : handleHeaderClose}
              className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* ─── AI Application Scanner — inline crawl flow (Connect → Auth →
              Scan → Complete). Opened from "Re-scan Application" (and auto-opened
              for un-synced projects) so the whole experience stays in ONE dialog
              instead of a separate overlay. ─── */}
          {scannerOpen && (
            <div className="flex-1 min-h-0 overflow-auto flex flex-col">
              <ScannerPanel
                versionId={versionId}
                projectId={projectId}
                initialUrl={effectiveAppUrl}
                onClose={handleScannerClose}
              />
            </div>
          )}

          {/* ─── Inline Generation View — Only mount when active ─── */}
          {!scannerOpen && showGenerating && !inlineSaveSuccess && (
            <div
              className="relative flex flex-col flex-1 min-h-0"
              style={{ contain: "layout paint" }}
            >
              {/* Generator stays mounted for the whole generating view so its
                  imperative ref (triggerGeneration) is available the instant the
                  auto-trigger effect fires. The "Preparing" screen overlays it
                  until the stream reports it has actually started. */}
              <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                <Suspense
                  fallback={
                    <PreparingGeneration
                      mode={outputMode}
                      projectName={projectName}
                      pageCount={pageCount}
                      interactionCount={interactionCount}
                      prompt={prompt}
                    />
                  }
                >
                  <TestCaseGeneration
                    ref={inlineGeneratorRef}
                    mode={outputMode}
                    embedded
                    enableBackground
                    onHandleGenerating={setInlineGenerating}
                    pathToActiveNode={pathToActiveNode}
                    onSelectedTestCases={(value: any[]) => {
                      setInlineSelectedTests(value);
                      onSelectedTestCases?.(value);
                    }}
                    onComplete={() => {
                      setInlineCompleted(true);
                      setInlineGenerating(false);
                      onGenerationComplete?.();
                    }}
                    onResetGeneration={() => {
                      // A generation that never actually started (entitlement
                      // rejection, validation error, network failure, etc.)
                      // must not leave the "Preparing…" overlay stuck on
                      // screen — drop back to the prompt view so the user
                      // sees why and can retry. Success goes through
                      // onComplete instead, which never calls this.
                      setShowGenerating(false);
                      setInlineGenerating(false);
                      setInlineCompleted(false);
                    }}
                    activeFolderId={activeFolderId}
                    initialPrompt={prompt || undefined}
                    requirementIds={linkedReqs.map((r) => r.id)}
                    uploadedFile={uploadedFile}
                    savingAll={inlineSaving}
                  />
                </Suspense>
              </div>

              {/* Overlay until generation actually begins streaming */}
              {!inlineGenerating && !inlineCompleted && !prepExpired && (
                <div className="absolute inset-0 z-10 flex flex-col bg-white">
                  <PreparingGeneration
                    mode={outputMode}
                    projectName={projectName}
                    pageCount={pageCount}
                    interactionCount={interactionCount}
                    prompt={prompt}
                  />
                </div>
              )}
            </div>
          )}

          {/* ─── Save Success View ─── */}
          {!scannerOpen && inlineSaveSuccess && (
            <div className="flex flex-col items-center justify-center flex-1 py-20 gap-4">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-success"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="text-center space-y-1">
                <h3 className="text-[15px] font-semibold text-foreground">
                  All test cases have been added successfully
                </h3>
                <p className="text-[13px] text-muted-foreground">
                  You can add details to the generated test cases and continue
                  testing
                </p>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setInlineSaveSuccess(false);
                    setShowGenerating(false);
                    setInlineGenerating(false);
                    setInlineCompleted(false);
                    setInlineSelectedTests([]);
                  }}
                  className="px-4 py-1.5 text-[12.5px] font-medium border border-border rounded-md hover:bg-muted transition-colors"
                >
                  Generate More Test Cases
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-1.5 text-[12.5px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors"
                >
                  Back to Test Cases
                </button>
              </div>
            </div>
          )}

          {/* ─── Generator prompt view ─── */}
          {!scannerOpen && !showGenerating && (
            <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
              {/* Sync Status — Collapsed pill (click to expand) */}
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="w-full flex items-center justify-between px-3 py-2 bg-muted/40 border border-border rounded-md hover:bg-muted/70 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  {hasSyncContext ? (
                    <span className="relative flex size-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-success/60 opacity-60 animate-ping" />
                      <span className="relative inline-flex size-1.5 rounded-full bg-success" />
                    </span>
                  ) : (
                    <span className="relative flex size-1.5">
                      <span className="relative inline-flex size-1.5 rounded-full bg-warning" />
                    </span>
                  )}
                  <span className="text-[12px] font-medium text-foreground">
                    {hasSyncContext
                      ? isPartialSync
                        ? "AI Site Sync active (partial)"
                        : "AI Site Sync active"
                      : "Sync incomplete — re-scan needed"}
                  </span>
                  <span className="text-[12px] text-info font-medium">
                    — {projectName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-medium text-muted-foreground">
                    {pageCount} pages
                  </span>
                  {expanded ? (
                    <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Sync Status — Expanded detail */}
              {expanded && (
                <div className="bg-muted/40 rounded-md border border-border p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-3">
                      <div className="size-10 bg-white border border-border rounded-md flex items-center justify-center">
                        <Building2
                          className="size-5 text-info"
                          strokeWidth={1.75}
                        />
                      </div>
                      <div>
                        <h3 className="text-[13px] font-semibold text-info">
                          {projectName}
                        </h3>
                        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                          {effectiveAppUrl}
                        </p>
                      </div>
                    </div>
                    {hasSyncContext ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-success px-2 py-0.5 bg-success/10 rounded-md border border-success/20">
                        Synced
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-warning px-2 py-0.5 bg-warning/10 rounded-md border border-warning/20">
                        Not synced
                      </span>
                    )}
                  </div>

                  {isPartialSync && (
                    <div className="mb-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-px text-warning" />
                      <span>
                        The last crawl was interrupted, so some pages may be
                        missing. Re-scan the application to refresh.
                      </span>
                    </div>
                  )}

                  {/* Application selector — show when multiple synced apps exist or a scanned URL is selected */}
                  {(syncedApps.length > 0 || selectedSyncUrl) && (
                    <div className="mb-3">
                      <p className="text-[10.5px] text-muted-foreground font-semibold mb-1.5 uppercase tracking-wide">
                        Synced Applications
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {/* Show a badge for selectedSyncUrl if it's not already in syncedApps */}
                        {selectedSyncUrl &&
                          !syncedApps.some(
                            (a) => a.appUrl === selectedSyncUrl,
                          ) && (
                            <button
                              type="button"
                              className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-info/10 border border-info/30 text-info cursor-pointer"
                            >
                              {(() => {
                                try {
                                  return new URL(selectedSyncUrl).hostname;
                                } catch {
                                  return selectedSyncUrl;
                                }
                              })()}
                              <span className="ml-1 text-[10px] text-muted-foreground">
                                (
                                {overrideSyncStatus?.pageCount ??
                                  effectivePages.length ??
                                  0}
                                p)
                              </span>
                            </button>
                          )}
                        {syncedApps.map((app) => {
                          // Immediate highlight based on selectedSyncUrl (no waiting for API)
                          const isActive = selectedSyncUrl
                            ? selectedSyncUrl === app.appUrl
                            : syncStatus?.appUrl === app.appUrl ||
                              (!syncStatus?.appUrl &&
                                syncStatus?.syncId &&
                                String(syncStatus.syncId) ===
                                  String(app.syncId));
                          return (
                            <button
                              key={app.syncId}
                              type="button"
                              onClick={() => {
                                if (isActive && selectedSyncUrl) {
                                  setSelectedSyncUrl(null);
                                } else if (!isActive) {
                                  setSelectedSyncUrl(app.appUrl);
                                }
                              }}
                              className={cn(
                                "px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors cursor-pointer",
                                isActive
                                  ? "bg-info/10 border border-info/30 text-info"
                                  : "bg-white border border-border text-muted-foreground hover:border-ring hover:bg-muted/50",
                              )}
                            >
                              {(() => {
                                try {
                                  return new URL(app.appUrl).hostname;
                                } catch {
                                  return app.appUrl;
                                }
                              })()}
                              <span className="ml-1 text-[10px] text-muted-foreground">
                                ({app.pageCount}p)
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {isOverrideLoading ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="bg-white p-3 rounded-md border border-border animate-pulse"
                          >
                            <div className="h-3 w-16 bg-muted rounded mb-2" />
                            <div className="h-6 w-10 bg-muted rounded" />
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="h-3 w-28 bg-muted rounded mb-2 animate-pulse" />
                        <div className="flex flex-wrap gap-1.5">
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className="h-7 w-20 bg-muted/60 border border-border rounded-md animate-pulse"
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        <Stat label="Pages" value={String(pageCount)} />
                        <Stat
                          label="Interactions"
                          value={String(interactionCount)}
                        />
                        <Stat
                          label="Auth"
                          value={authConfigured ? "Yes" : "None"}
                          accent={!!authConfigured}
                        />
                        <Stat label="Synced" value={lastSynced} />
                      </div>

                      {effectivePages.length > 0 ? (
                        <div>
                          <p className="text-[10.5px] text-muted-foreground font-semibold mb-2 uppercase tracking-wide">
                            Discovered pages
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {(showAllPages
                              ? effectivePages
                              : effectivePages.slice(0, 7)
                            ).map((p: any) => (
                              <span
                                key={p.routePath || p.url || p.name}
                                className="px-2 py-0.5 bg-white border border-border rounded-md text-[11px] font-medium text-foreground"
                              >
                                {p.name || p.routePath || p.url}
                              </span>
                            ))}
                            {effectivePages.length > 7 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllPages((value) => !value)
                                }
                                className="px-2 py-0.5 bg-muted/60 border border-border rounded-md text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                aria-expanded={showAllPages}
                              >
                                {showAllPages
                                  ? "Show fewer"
                                  : `+${effectivePages.length - 7} more`}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        !hasSyncContext && (
                          <div className="flex items-start gap-2 px-3 py-2 bg-warning/5 border border-warning/20 rounded-md">
                            <TriangleAlert className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              No pages were discovered for this application, so
                              there is no context to generate accurate tests.
                              Click{" "}
                              <span className="font-medium text-foreground">
                                Re-scan Application
                              </span>{" "}
                              to crawl the site again.
                            </p>
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Prompt Input Hero */}
              <div className="group relative">
                <div className="relative bg-white border border-border rounded-md transition-colors overflow-hidden focus-within:border-ring">
                  {/* Linked requirements — compact ID badges. Hover shows the
                      title/type tooltip; clicking the ID opens the requirement
                      in a new tab; the × unlinks it. Keeps the panel tiny. */}
                  {linkedReqs.length > 0 && (
                    <div className="border-b border-border bg-muted/40">
                      <div className="flex items-center justify-between px-4 pt-2.5 pb-1.5">
                        <div className="flex items-center gap-2">
                          <FileText size={12} className="text-info" />
                          <span className="text-[12px] font-semibold text-foreground">
                            {linkedReqs.length === 1
                              ? "1 Requirement linked"
                              : `${linkedReqs.length} Requirements linked`}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReqPickerOpen(true)}
                          className="text-[12px] text-info hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <PlusIcon size={11} /> Add
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 px-4 pb-2.5">
                        {linkedReqs.map((req) => (
                          <RequirementTooltip key={req.id} req={req}>
                            <span className="group/req inline-flex h-6 items-center gap-1 rounded-md border border-border bg-muted px-2 text-[11px] font-medium text-foreground/80 hover:bg-muted/70 transition-colors">
                              <a
                                href={`/test-management/requirements?requirementId=${req.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-[11px] font-medium text-primary cursor-pointer leading-none hover:underline"
                              >
                                REQ-{String(req.id).padStart(4, "0")}
                              </a>
                              <button
                                type="button"
                                aria-label="Unlink requirement"
                                onClick={() => {
                                  const updated = linkedReqs.filter(
                                    (r) => r.id !== req.id,
                                  );
                                  setLinkedReqs(updated);
                                  onRequirementIds?.(updated.map((r) => r.id));
                                }}
                                className="-mr-0.5 shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-destructive hover:bg-destructive/10 cursor-pointer"
                              >
                                <X size={11} />
                              </button>
                            </span>
                          </RequirementTooltip>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="p-4">
                    <label
                      htmlFor="synced-modal-prompt"
                      className="block text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wide mb-2"
                    >
                      Describe your testing requirements
                      {hasContext && (
                        <span className="ml-1.5 text-muted-foreground/60 normal-case tracking-normal font-normal">
                          (optional)
                        </span>
                      )}
                    </label>
                    <textarea
                      id="synced-modal-prompt"
                      value={prompt}
                      onChange={(e) => onPromptChange(e.target.value)}
                      className="w-full min-h-[140px] bg-transparent border-none p-0 focus:ring-0 focus:outline-none text-[13px] text-foreground placeholder:text-muted-foreground/60 resize-none leading-normal"
                      placeholder={promptPlaceholder}
                    />
                    {/* Context hint — what AI will generate from when no prompt typed */}
                    {hasContext && !prompt.trim() && (
                      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-info">
                        <Sparkles className="size-3 mt-0.5 shrink-0" />
                        <span>
                          {hasRequirement && hasDoc
                            ? `Tests will be generated from ${reqIdsLabel} and the uploaded document.`
                            : hasRequirement
                              ? `Tests will be generated from ${reqIdsLabel}.`
                              : "Tests will be generated from the uploaded document."}
                        </span>
                      </div>
                    )}
                    {testCaseSuggestions.length > 0 && (
                      <SuggestionChips
                        suggestions={testCaseSuggestions}
                        isSelected={suggestionChips.isSelected}
                        onSelect={suggestionChips.onSelect}
                      />
                    )}
                    {/* Discovered journeys section */}
                    {journeys.length > 0 && (
                      <div className="mt-3 border-t border-border pt-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5">
                            <Route size={12} className="text-info" />
                            <span className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wide">
                              Discovered Journeys
                            </span>
                          </div>
                          {selectedJourney && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedJourneyId(null);
                                onPromptChange("");
                              }}
                              className="text-[11px] text-muted-foreground hover:text-foreground underline"
                            >
                              Clear selection
                            </button>
                          )}
                        </div>
                        {selectedJourney && (
                          <div className="mb-3 text-[11px] text-muted-foreground">
                            Selected journey:{" "}
                            <span className="font-medium text-foreground">
                              {selectedJourney.name}
                            </span>
                            . You can choose another journey or edit the prompt
                            manually.
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {journeys.slice(0, 6).map((j: any) => (
                            <button
                              key={j.id || j.name}
                              type="button"
                              onClick={() => handleSelectJourney(j)}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]
                                bg-info/10 text-info border border-info/30 hover:bg-info/20
                                cursor-pointer transition-colors"
                              title={j.description || j.name}
                            >
                              <span
                                className={cn(
                                  "w-1.5 h-1.5 rounded-full shrink-0",
                                  j.priority === "critical"
                                    ? "bg-red-400"
                                    : j.priority === "high"
                                      ? "bg-orange-400"
                                      : j.priority === "medium"
                                        ? "bg-yellow-400"
                                        : "bg-green-400",
                                )}
                              />
                              {(j.name || "").length > 40
                                ? (j.name || "").slice(0, 40) + "…"
                                : j.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Integrated Toolbar */}
                  <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-border bg-muted/40 rounded-b-md">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Tabs
                        value={outputMode}
                        onValueChange={(value) =>
                          onOutputModeChange(value as OutputMode)
                        }
                        className="mr-1"
                      >
                        <TabsList className="h-auto rounded-md border border-border bg-muted p-0.5">
                          <TabsTrigger
                            value="manual"
                            className="px-3 py-1 text-[12px] font-medium rounded-[5px] data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                          >
                            Manual
                          </TabsTrigger>
                          <TabsTrigger
                            value="automation"
                            className="px-3 py-1 text-[12px] font-medium rounded-[5px] data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                          >
                            Automation
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>

                      {/* Link requirement */}
                      {linkedReqs.length === 0 && (
                        <ToolbarButton
                          icon={<Link2 className="size-3.5" />}
                          onClick={() => setReqPickerOpen(true)}
                        >
                          Link Requirement
                        </ToolbarButton>
                      )}
                    </div>

                    {/* File upload */}
                    <FileUpload onFileChange={handleFileUploadLocal} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer — hidden while the scanner is active (its phases carry their
              own actions). */}
          {!scannerOpen && !inlineSaveSuccess && (
            <footer className="px-5 py-2.5 bg-muted/40 border-t border-border flex items-center justify-between shrink-0">
              {showGenerating ? (
                /* ─── Generation footer ─── */
                <>
                  <button
                    type="button"
                    onClick={handleInlineStartOver}
                    className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    <ArrowLeft className="size-3.5" />
                    Start Over
                  </button>
                  <div className="flex items-center gap-2">
                    {/* While generating, the header X closes (backgrounds) the
                        modal — so we only surface a single "Cancel" action here
                        that actually aborts generation. */}
                    {!inlineGenerating &&
                      (inlineCompleted &&
                      !inlineSaving &&
                      (inlineSelectedTests?.length || 0) > 0 ? (
                        <button
                          type="button"
                          onClick={handleInlineCloseAndSaveInBackground}
                          className="px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground border border-border rounded-md hover:bg-muted hover:text-foreground transition-colors flex items-center gap-1.5 cursor-pointer"
                          title="Save the selected test cases in the background and close this dialog"
                        >
                          <CloudUpload className="size-3.5" />
                          Close &amp; Save in Background
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        >
                          Close
                        </button>
                      ))}
                    {inlineGenerating ? (
                      <button
                        type="button"
                        onClick={handleInlineStopGenerating}
                        className="px-3 py-1.5 text-[12.5px] font-medium text-foreground border border-border rounded-md hover:bg-muted transition-colors flex items-center gap-2 cursor-pointer"
                      >
                        <Sparkles className="size-3.5 animate-spin text-info" />
                        Cancel
                      </button>
                    ) : inlineCompleted ? (
                      <button
                        type="button"
                        onClick={handleInlineSave}
                        disabled={inlineSaving}
                        className="px-4 py-1.5 text-[12.5px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {inlineSaving ? (
                          <>
                            <svg
                              className="animate-spin size-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Sparkles className="size-3.5" />
                            {`Add ${inlineSelectedTests?.length || 0} Test Cases`}
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>
                </>
              ) : (
                /* ─── Prompt footer ─── */
                <>
                  <button
                    type="button"
                    onClick={() => setScannerOpen(true)}
                    disabled={syncInProgress}
                    title={
                      syncInProgress
                        ? "A scan is already running for this application"
                        : undefined
                    }
                    className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="size-3.5" />
                    Re-scan Application
                  </button>
                  <div className="flex items-center gap-2">
                    {syncInProgress && (
                      <span className="text-[12px] text-muted-foreground flex items-center gap-1.5">
                        <Radar className="size-3.5 animate-pulse text-info" />
                        Synchronising application…
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={
                        (!prompt.trim() && !hasContext) ||
                        syncInProgress ||
                        !hasSyncContext
                      }
                      title={
                        syncInProgress
                          ? "Generation becomes available once synchronisation finishes"
                          : !aiEnabled
                            ? "Upgrade to unlock AI generation"
                            : !hasSyncContext
                              ? "Point AI at your application first"
                              : undefined
                      }
                      onClick={handleGenerateClick}
                      className="px-4 py-1.5 text-[12.5px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="size-3.5" />
                      {outputMode === "automation"
                        ? "Generate Automation Test"
                        : "Generate Manual Test"}
                    </button>
                  </div>
                </>
              )}
            </footer>
          )}
        </section>
      </div>

      {/* Requirement picker — shared 3-pane folder dialog (Ready-only). */}
      {reqPickerOpen && (
        <EntityLinkDialog
          open={reqPickerOpen}
          onClose={() => setReqPickerOpen(false)}
          mode="requirement"
          versionId={versionId}
          alreadyLinkedIds={linkedReqs.map((r) => r.id)}
          onConfirm={(ids, rows) => {
            const existing = new Set(linkedReqs.map((r) => r.id));
            const additions = (rows ?? [])
              .filter((r) => !existing.has(r.id))
              .map((r) => ({
                id: r.id,
                name: r.name,
                requirementtype: r.typeValue,
              }));
            const updated = [...linkedReqs, ...additions];
            setLinkedReqs(updated);
            onRequirementIds?.(updated.map((r) => r.id));
          }}
          onUnlink={(ids) => {
            const removeSet = new Set(ids);
            const updated = linkedReqs.filter((r) => !removeSet.has(r.id));
            setLinkedReqs(updated);
            onRequirementIds?.(updated.map((r) => r.id));
          }}
        />
      )}

      {/* Discard-generation confirmation — guards the header X after a finished
          but unsaved generation so the user doesn't lose the generated cases. */}
      <AlertDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        icon={<TriangleAlert className="h-5 w-5" />}
        title="Discard generated test cases?"
        description="Generation is complete but these test cases haven't been saved. If you close now they'll be lost. You can add them, or save them in the background instead."
        confirmText="Discard & Close"
        cancelText="Keep Reviewing"
        confirmColor="red"
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
};

export default SyncedGeneratorModal;
